import { useAuth } from '@shared/auth';
import { useTranslation } from '@shared/localization';
import cn from 'clsx';
import { useEffect, useRef, useState, JSX, RefObject } from 'react';

import { disableUIBlocker } from '@/components/common';
import { PayButton } from '@/components/pages/stored-instruments/utils/PayButton';
import { triggerErrorToast } from '@/libs/toasts';
import { PaymentIntentFlow } from '@/schemas/PaymentIntent';
import { PostFinanceMandateDetails } from '@/services/directdebit/schemas';
import NavigationService, {
  InternalPage,
} from '@/services/navigation/NavigationService';
import PaymentStore from '@/services/payments/PaymentStore';
import {
  isValidIban,
  isValidIbanFormat,
} from '@/services/sepa/helpers/IbanValidationHelper';
import { getQaTestingProps, QaTestingElements } from '@/utils/qaTesting';

interface IPostFinanceFormProps {
  navigation?: JSX.Element | null;
}

export function PostFinanceForm({ navigation }: IPostFinanceFormProps) {
  const { t } = useTranslation();
  const { accessToken, userId, givenName, familyName } = useAuth();

  const bankName = 'Postfinance';
  const [ibanValue, setIbanValue] = useState('');
  const [ibanValid, setIbanValid] = useState(false);

  const ibanFieldRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    const customerNumber = userId;
    if (customerNumber === undefined) {
      throw new Error('customerNumber is undefined!');
    }

    let iban = ibanValue?.trim();
    iban = iban.replace(/\s+/g, '').toUpperCase();
    if (iban?.length <= 0) {
      markIbanFieldInvalid(ibanFieldRef);
    } else {
      if (isValidIbanFormat(iban)) {
        markIbanFieldValid(ibanFieldRef);
      } else {
        markIbanFieldInvalid(ibanFieldRef);
      }
    }

    function markIbanFieldValid(ibanRef: RefObject<HTMLInputElement | null>) {
      setIbanValid(true);
      markFieldValid(ibanRef);
    }

    function markIbanFieldInvalid(ibanRef: RefObject<HTMLInputElement | null>) {
      setIbanValid(false);
      markFieldInvalid(ibanRef);
    }
  }, [ibanValue]);

  function markFieldInvalid(fieldRef: RefObject<HTMLInputElement | null>) {
    fieldRef.current?.classList?.remove('border-transparent');
    fieldRef.current?.classList?.add('border-error');
  }

  function markFieldValid(fieldRef: RefObject<HTMLInputElement | null>) {
    fieldRef.current?.classList?.remove('border-error');
    fieldRef.current?.classList?.add('border-transparent');
  }

  const customerName =
    PaymentStore.customerName || `${givenName} ${familyName}`;

  const setMandateDetailsAndNavigateToSummary = async () => {
    const ibanIsValid = await isValidIban(ibanValue, accessToken ?? '').catch(
      () => false,
    );
    disableUIBlocker();

    if (!ibanIsValid) {
      triggerErrorToast(t('Invalid IBAN'));
      markFieldInvalid(ibanFieldRef);
      setIbanValid(false);
      return;
    }

    const postFinanceMandateDetails: PostFinanceMandateDetails = {
      iban: ibanValue,
      customerName: customerName,
      bankName: bankName,
    };

    PaymentStore.directDebitMandate = postFinanceMandateDetails;
    NavigationService.navigateTo({
      page: InternalPage.PostFinanceMandateSummary,
    });
  };

  const styles = {
    input:
      'mt-2 relative rounded-field bg-secondary h-14 p-sm focused-container w-full border-2 border-transparent ring-1 ring-silver-700 bg-secondary shadow-on-focus',
    buttonContainer: 'flex mt-md space-x-2',
    outerContainer: 'p-sm bg-white ring-1 ring-silver-700 rounded-md',
  };

  return (
    <div className={cn(styles.outerContainer)}>
      <input
        id="accountHolderName"
        type="text"
        value={customerName}
        className={cn('mt-2', styles.input)}
        disabled={true}
        {...getQaTestingProps(
          QaTestingElements.AccountHolderName,
          customerName,
        )}
      />
      <input
        id="iban"
        placeholder="IBAN"
        type="text"
        value={ibanValue}
        onChange={(e) => setIbanValue(e.target.value)}
        className={cn(styles.input)}
        ref={ibanFieldRef}
        {...getQaTestingProps(QaTestingElements.AccountIban, ibanValue)}
      />
      <input
        id="bankName"
        type="text"
        value={bankName}
        className={cn(styles.input)}
        disabled={true}
        {...getQaTestingProps(QaTestingElements.AccountBankName, bankName)}
      />
      <div className={cn(styles.buttonContainer)}>
        {navigation}
        <PayButton
          onClick={() => setMandateDetailsAndNavigateToSummary()}
          title={
            PaymentStore.flow === PaymentIntentFlow.Manage
              ? t('Add new PostFinance mandate')
              : t('Proceed to pay')
          }
          disabled={!ibanValid}
        />
      </div>
    </div>
  );
}
